import { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import { TrendingUp, PiggyBank } from "lucide-react";
import { DEFAULT_HOURLY_RATE } from "../data/automationData";

export const RoiCalculator = ({ suggestedHours, toolsCost, businessType, painPoints, onSave }) => {
  const [hours, setHours] = useState(Math.round(suggestedHours) || 10);
  const [rate, setRate] = useState(DEFAULT_HOURLY_RATE);
  const [saved, setSaved] = useState(false);

  const { monthlyValue, monthlyNet, yearlyNet } = useMemo(() => {
    const weeklyValue = hours * rate;
    const monthlyValue = weeklyValue * 4.33;
    const monthlyNet = Math.max(monthlyValue - toolsCost, 0);
    return {
      monthlyValue: Math.round(monthlyValue),
      monthlyNet: Math.round(monthlyNet),
      yearlyNet: Math.round(monthlyNet * 12),
    };
  }, [hours, rate, toolsCost]);

  const handleSave = () => {
    onSave?.({
      business_type: businessType,
      pain_points: painPoints,
      hours_per_week: hours,
      hourly_rate: rate,
      monthly_savings: monthlyNet,
    });
    setSaved(true);
  };

  return (
    <div className="bg-primary text-primary-foreground rounded-2xl p-6 md:p-8 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <PiggyBank className="h-5 w-5" />
        <h3 className="font-heading text-xl font-semibold">Tu calculadora de retorno (ROI)</h3>
      </div>
      <p className="text-sm text-primary-foreground/70 mb-8">
        Ajusta a tu semana real. Descubre cuánto vale tu tiempo.
      </p>

      <div className="space-y-8">
        <div>
          <div className="flex justify-between mb-3">
            <label className="text-sm font-medium">Horas que ahorrarías por semana</label>
            <span className="font-heading font-bold text-lg">{hours} hrs</span>
          </div>
          <Slider
            data-testid="roi-hours-slider"
            value={[hours]}
            min={1}
            max={40}
            step={1}
            onValueChange={(v) => {
              setHours(v[0]);
              setSaved(false);
            }}
          />
        </div>

        <div>
          <div className="flex justify-between mb-3">
            <label className="text-sm font-medium">Cuánto vale tu tiempo (por hora)</label>
            <span className="font-heading font-bold text-lg">${rate}</span>
          </div>
          <Slider
            data-testid="roi-rate-slider"
            value={[rate]}
            min={10}
            max={150}
            step={5}
            onValueChange={(v) => {
              setRate(v[0]);
              setSaved(false);
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-8">
        <div className="rounded-xl bg-primary-foreground/10 p-4">
          <p className="text-xs text-primary-foreground/70">Valor de tu tiempo / mes</p>
          <p className="font-heading font-bold text-2xl mt-1">${monthlyValue.toLocaleString()}</p>
        </div>
        <div className="rounded-xl bg-accent p-4">
          <p className="text-xs text-accent-foreground/80 flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" /> Neto / mes tras herramientas
          </p>
          <p
            className="font-heading font-bold text-2xl mt-1 text-accent-foreground"
            data-testid="roi-monthly-net"
          >
            ${monthlyNet.toLocaleString()}
          </p>
        </div>
      </div>

      <p className="text-sm text-primary-foreground/80 mt-4">
        Eso es cerca de{" "}
        <span className="font-semibold text-primary-foreground">
          ${yearlyNet.toLocaleString()}/año
        </span>{" "}
        de vuelta en tu bolsillo, después de pagar ~${toolsCost}/mes en herramientas.
      </p>

      <button
        onClick={handleSave}
        disabled={saved}
        data-testid="roi-save-btn"
        className="mt-6 w-full bg-primary-foreground text-primary py-3 rounded-full font-semibold hover:bg-primary-foreground/90 transition-colors disabled:opacity-60"
      >
        {saved ? "Guardado ✓" : "Guardar mis resultados"}
      </button>
    </div>
  );
};
